import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import {
  Plus, Trash2, RefreshCw, Search, X as XIcon, Building2, MapPin, DollarSign,
  Film, Image as ImageIcon, Plane, Bed, Utensils, Car, ExternalLink, Mail, Phone,
  Pencil, Upload, Calendar as CalendarIcon, Link2, Globe,
} from "lucide-react";
import { toast } from "sonner";
import MuxVideoPicker from "../components/MuxVideoPicker";

const STATUSES = [
  { v: "new_lead",    label: "🟡 New Lead",    color: "#fbbf24" },
  { v: "negotiating", label: "🟠 Negotiating", color: "#f97316" },
  { v: "confirmed",   label: "🟢 Confirmed",   color: "#22c55e" },
  { v: "scheduled",   label: "📅 Scheduled",   color: "#06b6d4" },
  { v: "shooting",    label: "🎬 Shooting",    color: "#a855f7" },
  { v: "wrapped",     label: "✅ Wrapped",     color: "#14b8a6" },
  { v: "edited",      label: "✂️ Edited",      color: "#84cc16" },
  { v: "released",    label: "🚀 Released",    color: "#10b981" },
  { v: "paid",        label: "💰 Paid",        color: "#16a34a" },
  { v: "rejected",    label: "🔴 Rejected",    color: "#dc2626" },
  { v: "archived",    label: "🗄 Archived",    color: "#6b7280" },
];
const CURRENCIES = ["EUR", "USD", "GBP"];
const MAX_SCENES = 20;

const labelFor = (k) => STATUSES.find(s => s.v === k)?.label || k;
const colorFor = (k) => STATUSES.find(s => s.v === k)?.color || "#6b7280";

const emptyStudio = () => ({
  studio_name: "", linked_crm_id: null, status: "new_lead",
  cover_image: "", photos: [], location: "",
  paid_accommodation: false, paid_flights: false, paid_food: false, paid_transport: false,
  fee_amount: 0, fee_currency: "EUR", contract_terms: "",
  contact_name: "", contact_email: "", contact_phone: "", website: "",
  scenes: [], tags: [], notes: "",
});

const newScene = () => ({
  id: `s_${Math.random().toString(36).slice(2, 8)}`,
  name: "", date: "", revenue: 0, revenue_currency: "EUR",
  cast_crm_ids: [], catalog_video_id: null, mux_playback_id: null, mux_asset_id: null, mux_duration: null,
  photo_urls: [], notes: "",
});

export default function Studios() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [crm, setCrm] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyStudio());
  const [query, setQuery] = useState("");
  const [fltStatus, setFltStatus] = useState("all");

  const load = async () => {
    const [l, s, c] = await Promise.all([
      api.get("/api/studios/"),
      api.get("/api/studios/summary"),
      api.get("/api/crm/").catch(() => ({ data: [] })),
    ]);
    setItems(l.data); setSummary(s.data); setCrm(c.data);
  };
  useEffect(() => { load(); }, []);

  // Auto-open a specific studio when /studios?focus={id} is provided
  // (used by Finance "Open studio" cross-link on studio_payout rows).
  useEffect(() => {
    const fid = new URLSearchParams(window.location.search).get("focus");
    if (!fid || !items.length) return;
    const target = items.find(s => s.id === fid);
    if (target) openEdit(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const studios = useMemo(() => crm.filter(c => c.type === "Studio"), [crm]);

  const openNew = () => { setEditing(null); setForm(emptyStudio()); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...emptyStudio(), ...s }); setOpen(true); };
  const remove = async (id) => {
    if (!window.confirm("Delete this studio entry?")) return;
    await api.delete(`/api/studios/${id}`);
    load();
  };

  const save = async () => {
    if (!form.studio_name.trim()) return toast.error("Studio name required");
    if ((form.scenes || []).length > MAX_SCENES) return toast.error(`Max ${MAX_SCENES} scenes`);
    const payload = {
      ...form,
      fee_amount: Number(form.fee_amount) || 0,
      scenes: (form.scenes || []).map(s => ({ ...s, revenue: Number(s.revenue) || 0 })),
    };
    try {
      if (editing) await api.put(`/api/studios/${editing.id}`, payload);
      else await api.post("/api/studios/", payload);
      await load();
      setOpen(false);
      toast.success(editing ? "Updated" : "Studio logged");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return items.filter(s => {
      if (fltStatus !== "all" && s.status !== fltStatus) return false;
      if (!q) return true;
      return (`${s.studio_name} ${s.location} ${s.notes || ""} ${(s.tags || []).join(" ")}`).toLowerCase().includes(q);
    });
  }, [items, query, fltStatus]);

  return (
    <AppLayout title="Studios" overline="180 · STUDIO COLLABS · DEAL PIPELINE + SCENES + REVENUE">
      {/* KPI strip */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 fade-up" data-testid="studios-summary">
        <Stat label="Studios"        value={summary?.total ?? 0} testid="studios-stat-total" />
        <Stat label="Open leads"     value={summary?.leads_open ?? 0} testid="studios-stat-leads" accent />
        <Stat label="Total scenes"   value={summary?.scenes_total ?? 0} testid="studios-stat-scenes" />
        <Stat label="Revenue (EUR)"  value={(summary?.revenue_by_currency?.EUR ?? 0).toLocaleString()} testid="studios-stat-rev-eur" />
        <Stat label="Revenue (USD)"  value={(summary?.revenue_by_currency?.USD ?? 0).toLocaleString()} testid="studios-stat-rev-usd" />
      </section>

      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="studios-toolbar">
        <div className="flex items-center gap-2 border border-zinc-900 bg-zinc-950 px-3 py-2 flex-1 min-w-[240px]">
          <Search size={14} className="text-zinc-600" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search studio, location, notes..."
            data-testid="studios-search"
            className="bg-transparent text-white text-sm focus:outline-none flex-1" />
        </div>
        <select value={fltStatus} onChange={(e) => setFltStatus(e.target.value)} data-testid="studios-flt-status"
          className="bg-zinc-950 border border-zinc-900 text-white text-xs font-mono-ui uppercase tracking-wider py-2 px-3 focus:outline-none">
          <option value="all">All statuses</option>
          {STATUSES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <button onClick={() => load()} data-testid="studios-refresh" className="btn-ghost inline-flex items-center gap-2"><RefreshCw size={14} /></button>
        <button onClick={openNew} data-testid="studios-add-button" className="btn-primary inline-flex items-center gap-2">
          <Plus size={14} /> New studio lead
        </button>
      </div>

      {/* Grid of studio cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="studios-grid">
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 text-zinc-600">
            {items.length === 0
              ? "No studios yet. Click 'New studio lead' to start your first deal."
              : "No matches."}
          </div>
        )}
        {filtered.map(s => {
          const sceneCount = (s.scenes || []).length;
          const totalRevenue = (s.scenes || []).reduce((acc, sc) => acc + (Number(sc.revenue) || 0), 0);
          const linked = crm.find(c => c.id === s.linked_crm_id);
          return (
            <div key={s.id} data-testid={`studio-card-${s.id}`}
              className="group rounded-xl overflow-hidden border bg-zinc-950 hover:-translate-y-0.5 transition"
              style={{ borderColor: "var(--gio-border)" }}>
              {/* Cover */}
              <div className="relative aspect-[16/9] bg-black">
                {s.cover_image ? (
                  <img src={s.cover_image} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : s.scenes?.[0]?.mux_playback_id ? (
                  <img src={`https://image.mux.com/${s.scenes[0].mux_playback_id}/thumbnail.jpg?width=480&height=270&fit_mode=smartcrop`}
                    alt="" className="w-full h-full object-cover" />
                ) : s.photos?.[0] ? (
                  <img src={s.photos[0]} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Building2 size={32} className="text-zinc-700" /></div>
                )}
                <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full backdrop-blur"
                  style={{ background: `${colorFor(s.status)}E0`, color: "#000" }}>
                  {labelFor(s.status)}
                </span>
                {/* Hover actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                  <button onClick={() => openEdit(s)} data-testid={`studio-edit-${s.id}`}
                    className="px-3 py-1.5 rounded text-xs font-bold bg-white text-black inline-flex items-center gap-1">
                    <Pencil size={11} /> Edit
                  </button>
                  <button onClick={() => remove(s.id)} data-testid={`studio-del-${s.id}`}
                    className="px-3 py-1.5 rounded text-xs font-bold text-white" style={{ background: "rgba(220,38,38,0.95)" }}>
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
              {/* Body */}
              <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-white font-heading text-lg truncate">{s.studio_name}</div>
                    {s.location && <div className="text-[11px] inline-flex items-center gap-1 mt-0.5" style={{ color: "var(--gio-muted-2)" }}><MapPin size={10} /> {s.location}</div>}
                  </div>
                  {linked && (
                    <span className="text-[10px] font-mono-ui px-1.5 py-0.5 rounded inline-flex items-center gap-1 shrink-0" style={{ background: "var(--gio-primary-soft)", color: "var(--gio-primary)" }} title="Linked CRM record">
                      <Link2 size={9} /> CRM
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-2 text-[11px] flex-wrap" style={{ color: "var(--gio-muted-2)" }}>
                  <span className="inline-flex items-center gap-1"><Film size={11} /> {sceneCount}/{MAX_SCENES}</span>
                  {totalRevenue > 0 && <span className="inline-flex items-center gap-1 text-[color:var(--gio-secondary)]"><DollarSign size={11} /> {totalRevenue.toLocaleString()}</span>}
                  {s.fee_amount > 0 && <span className="inline-flex items-center gap-1">fee {s.fee_amount} {s.fee_currency}</span>}
                </div>
                {/* Perks badges */}
                <div className="flex flex-wrap gap-1 mt-2">
                  {s.paid_flights        && <Perk icon={Plane}      label="Flights" />}
                  {s.paid_accommodation  && <Perk icon={Bed}        label="Hotel" />}
                  {s.paid_food           && <Perk icon={Utensils}   label="Food" />}
                  {s.paid_transport      && <Perk icon={Car}        label="Transport" />}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {open && (
        <StudioModal
          form={form} setForm={setForm} editing={editing}
          studios={studios} crm={crm}
          onClose={() => setOpen(false)} onSave={save}
        />
      )}
    </AppLayout>
  );
}

function Stat({ label, value, testid, accent }) {
  return (
    <div className={`border bg-zinc-950 p-4 ${accent ? "border-[color:var(--gio-primary)]" : "border-zinc-900"}`} data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`font-heading text-3xl mt-1 ${accent ? "text-[color:var(--gio-primary)]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function Perk({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono-ui"
      style={{ background: "rgba(20,184,166,0.12)", color: "#14B8A6" }}>
      <Icon size={9} /> {label}
    </span>
  );
}

function StudioModal({ form, setForm, editing, studios, crm, onClose, onSave }) {
  const [tab, setTab] = useState("deal"); // deal | scenes | media
  const [catalogVideos, setCatalogVideos] = useState([]);

  useEffect(() => {
    let active = true;
    api.get("/api/catalog")
      .then((response) => {
        if (active) setCatalogVideos(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        if (active) setCatalogVideos([]);
      });
    return () => { active = false; };
  }, []);

  const updateField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateScene = (i, k, v) => setForm(f => ({
    ...f,
    scenes: f.scenes.map((s, j) => j === i ? { ...s, [k]: v } : s),
  }));
  const linkSceneToCatalog = (i, videoId) => {
    const video = catalogVideos.find((item) => item.id === videoId);
    setForm((current) => ({
      ...current,
      scenes: current.scenes.map((scene, j) => j === i ? {
        ...scene,
        catalog_video_id: videoId || null,
        ...(video ? { name: video.title || scene.name } : {}),
      } : scene),
    }));
  };
  const addScene = () => {
    if ((form.scenes || []).length >= MAX_SCENES) return toast.error(`Max ${MAX_SCENES} scenes`);
    setForm(f => ({ ...f, scenes: [...(f.scenes || []), newScene()] }));
  };
  const removeScene = (i) => setForm(f => ({ ...f, scenes: f.scenes.filter((_, j) => j !== i) }));

  // Cloudinary upload — server-side proxy (handles HEIC/iPhone/RAW, surfaces real errors)
  const uploadCloud = async (file, folder = "studios/photos") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", folder);
    fd.append("resource_type", "auto");
    const r = await api.post("/api/cloudinary/upload", fd);
    return r.data.secure_url;
  };

  const onUploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const urls = [];
      for (const f of files.slice(0, 20)) urls.push(await uploadCloud(f));
      setForm(f => ({ ...f, photos: [...(f.photos || []), ...urls] }));
    } catch (err) { toast.error(`Upload failed: ${err?.response?.data?.detail || err.message}`); }
    e.target.value = "";
  };
  const onUploadCover = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    try {
      const url = await uploadCloud(f, "studios/covers");
      setForm(s => ({ ...s, cover_image: url }));
    } catch (err) { toast.error(`Upload failed: ${err?.response?.data?.detail || err.message}`); }
    e.target.value = "";
  };
  const onScenePhotos = async (i, e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const urls = [];
      for (const f of files.slice(0, 10)) urls.push(await uploadCloud(f, "studios/scenes"));
      setForm(prev => ({
        ...prev,
        scenes: prev.scenes.map((s, j) => j === i ? { ...s, photo_urls: [...(s.photo_urls || []), ...urls] } : s),
      }));
    } catch (err) { toast.error(`Upload failed: ${err?.response?.data?.detail || err.message}`); }
    e.target.value = "";
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="studio-modal">
      <div className="bg-zinc-950 border border-zinc-800 max-w-3xl w-full my-8 rounded-xl flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-zinc-900 flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="overline text-[color:var(--gio-primary)]">{editing ? "EDIT" : "NEW"} · STUDIO COLLAB</div>
            <input value={form.studio_name} onChange={(e) => updateField("studio_name", e.target.value)}
              data-testid="studio-f-name"
              placeholder="Studio name (e.g. Lucas Entertainment)"
              className="mt-1 bg-transparent text-white font-heading text-2xl border-b border-zinc-800 focus:outline-none focus:border-[color:var(--gio-primary)] w-full" />
            {form.payout_synced_at && (
              <div className="mt-1.5 text-[10px] inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded font-mono-ui"
                style={{ background: "rgba(22,163,74,0.18)", color: "#22c55e" }}
                data-testid="studio-f-payout-synced">
                💰 PAYOUT MIRRORED TO FINANCE · {form.payout_synced_at.slice(0, 10)}
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="studio-modal-close" className="ml-3 text-zinc-500 hover:text-white"><XIcon size={20} /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-900" data-testid="studio-tabs">
          {[{ v: "deal", l: "Deal Terms" }, { v: "scenes", l: `Scenes · ${(form.scenes || []).length}/${MAX_SCENES}` }, { v: "media", l: "Media & Notes" }].map(t => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`studio-tab-${t.v}`}
              className="px-4 py-3 text-[12px] font-bold uppercase tracking-wider border-b-2 transition"
              style={{
                color: tab === t.v ? "var(--gio-primary)" : "var(--gio-muted-2)",
                borderColor: tab === t.v ? "var(--gio-primary)" : "transparent",
              }}>
              {t.l}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {tab === "deal" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <F l="Status">
                  <select data-testid="studio-f-status" value={form.status} onChange={(e) => updateField("status", e.target.value)} className="gio-input">
                    {STATUSES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select>
                  <div className="text-[10px] mt-1" style={{ color: "var(--gio-muted-3)" }}>
                    💡 Setting status to <span style={{ color: "#22c55e" }}>"Paid"</span> auto-creates revenue entries from each scene (idempotent).
                  </div>
                </F>
                <F l="Linked CRM record">
                  <select data-testid="studio-f-crm" value={form.linked_crm_id || ""} onChange={(e) => updateField("linked_crm_id", e.target.value || null)} className="gio-input">
                    <option value="">— Optional —</option>
                    <optgroup label="Studios">
                      {studios.map(c => <option key={c.id} value={c.id}>🏢 {c.stage_name}</option>)}
                    </optgroup>
                    <optgroup label="Other CRM">
                      {crm.filter(c => c.type !== "Studio").slice(0, 50).map(c => (
                        <option key={c.id} value={c.id}>{c.stage_name} · {c.type}</option>
                      ))}
                    </optgroup>
                  </select>
                </F>
                <F l="Location">
                  <input data-testid="studio-f-location" value={form.location} onChange={(e) => updateField("location", e.target.value)} placeholder="City / Country" className="gio-input" />
                </F>
                <F l="Website">
                  <input data-testid="studio-f-website" value={form.website} onChange={(e) => updateField("website", e.target.value)} placeholder="https://..." className="gio-input" />
                </F>
              </div>

              <div className="border-t border-zinc-900 pt-4">
                <div className="overline mb-2 text-[color:var(--gio-secondary)]">DEAL · WHAT'S INCLUDED?</div>
                <div className="grid grid-cols-2 gap-2">
                  <Toggle icon={Plane}    label="Paid flights"        checked={form.paid_flights}        onChange={(v) => updateField("paid_flights", v)}        testid="studio-f-flights" />
                  <Toggle icon={Bed}      label="Paid accommodation"  checked={form.paid_accommodation}  onChange={(v) => updateField("paid_accommodation", v)}  testid="studio-f-accommodation" />
                  <Toggle icon={Utensils} label="Paid food / per-diem" checked={form.paid_food}           onChange={(v) => updateField("paid_food", v)}            testid="studio-f-food" />
                  <Toggle icon={Car}      label="Paid transport"      checked={form.paid_transport}      onChange={(v) => updateField("paid_transport", v)}      testid="studio-f-transport" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <F l="Talent fee">
                  <input data-testid="studio-f-fee" type="number" min="0" value={form.fee_amount} onChange={(e) => updateField("fee_amount", parseFloat(e.target.value) || 0)} className="gio-input" />
                </F>
                <F l="Currency">
                  <select data-testid="studio-f-currency" value={form.fee_currency} onChange={(e) => updateField("fee_currency", e.target.value)} className="gio-input">
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </F>
                <F l="Tags (comma)">
                  <input data-testid="studio-f-tags" value={Array.isArray(form.tags) ? form.tags.join(", ") : (form.tags || "")} onChange={(e) => updateField("tags", e.target.value.split(",").map(t => t.trim()).filter(Boolean))} placeholder="bareback, milan, group" className="gio-input" />
                </F>
              </div>

              <div className="border-t border-zinc-900 pt-4">
                <div className="overline mb-2">CONTACT</div>
                <div className="grid grid-cols-3 gap-3">
                  <F l="Contact name"><input data-testid="studio-f-contact-name" value={form.contact_name} onChange={(e) => updateField("contact_name", e.target.value)} className="gio-input" /></F>
                  <F l="Email"><input data-testid="studio-f-contact-email" value={form.contact_email} onChange={(e) => updateField("contact_email", e.target.value)} className="gio-input" /></F>
                  <F l="Phone"><input data-testid="studio-f-contact-phone" value={form.contact_phone} onChange={(e) => updateField("contact_phone", e.target.value)} className="gio-input" /></F>
                </div>
              </div>

              <F l="Contract terms / negotiation notes">
                <textarea data-testid="studio-f-contract" value={form.contract_terms} onChange={(e) => updateField("contract_terms", e.target.value)} rows={3} placeholder="Talent fee terms, residuals, exclusivity, royalty splits…" className="gio-input" />
              </F>
            </>
          )}

          {tab === "scenes" && (
            <div className="space-y-3">
              {(form.scenes || []).length === 0 && (
                <div className="text-center py-8 text-zinc-600 italic">No scenes yet — add up to {MAX_SCENES}.</div>
              )}
              {(form.scenes || []).map((sc, i) => (
                <div key={sc.id || i} className="border border-zinc-900 rounded-lg p-3 bg-black/40" data-testid={`studio-scene-${i}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] font-mono-ui px-1.5 py-0.5 rounded font-bold"
                      style={{ background: "var(--gio-primary-soft)", color: "var(--gio-primary)" }}>
                      SCENE #{i + 1}
                    </span>
                    {sc.linked_revenue_id && (
                      <span className="text-[9px] font-mono-ui px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                        style={{ background: "rgba(22,163,74,0.18)", color: "#22c55e" }}
                        data-testid={`studio-scene-paid-${i}`} title="Mirrored into Finance ledger">
                        💰 PAID
                      </span>
                    )}
                    <input value={sc.name} onChange={(e) => updateScene(i, "name", e.target.value)}
                      placeholder="Scene name"
                      data-testid={`studio-scene-name-${i}`}
                      className="gio-input flex-1" />
                    <button onClick={() => removeScene(i)} type="button" data-testid={`studio-scene-del-${i}`}
                      className="text-zinc-600 hover:text-[color:var(--gio-primary)]"><Trash2 size={14} /></button>
                  </div>
                  <div className="mb-2">
                    <div className="overline mb-1.5">Catalog video</div>
                    <select value={sc.catalog_video_id || ""} onChange={(e) => linkSceneToCatalog(i, e.target.value)}
                      data-testid={`studio-scene-catalog-${i}`} className="gio-input">
                      <option value="">— Not linked —</option>
                      {catalogVideos.map((video) => (
                        <option key={video.id} value={video.id}>{video.title || video.name || `Video ${video.id}`}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <F l="Shoot date">
                      <input type="date" value={sc.date || ""} onChange={(e) => updateScene(i, "date", e.target.value)}
                        data-testid={`studio-scene-date-${i}`} className="gio-input" />
                    </F>
                    <F l="Revenue">
                      <input type="number" min="0" value={sc.revenue || 0} onChange={(e) => updateScene(i, "revenue", parseFloat(e.target.value) || 0)}
                        data-testid={`studio-scene-rev-${i}`} className="gio-input" />
                    </F>
                    <F l="Currency">
                      <select value={sc.revenue_currency || "EUR"} onChange={(e) => updateScene(i, "revenue_currency", e.target.value)}
                        data-testid={`studio-scene-cur-${i}`} className="gio-input">
                        {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </F>
                  </div>
                  <div className="mt-2">
                    <div className="overline mb-1.5 inline-flex items-center gap-1.5"><Film size={11} /> Scene video</div>
                    <MuxVideoPicker
                      value={sc.mux_playback_id}
                      onChange={(playback_id) => updateScene(i, "mux_playback_id", playback_id)}
                      testidPrefix={`studio-scene-mux-${i}`}
                      compact
                    />
                  </div>
                  <div className="mt-2">
                    <div className="overline mb-1">Photos · {sc.photo_urls?.length || 0}</div>
                    <div className="grid grid-cols-6 gap-1.5">
                      {(sc.photo_urls || []).map((u, j) => (
                        <div key={j} className="relative group rounded overflow-hidden" data-testid={`studio-scene-photo-${i}-${j}`}>
                          <img src={u} alt="" className="w-full h-14 object-cover" />
                          <button onClick={() => updateScene(i, "photo_urls", sc.photo_urls.filter((_, k) => k !== j))} type="button"
                            className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white">
                            <Trash2 size={10} />
                          </button>
                        </div>
                      ))}
                      <label className="flex flex-col items-center justify-center h-14 border-2 border-dashed cursor-pointer text-[9px] hover:bg-[var(--gio-primary-soft)]"
                        style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }} data-testid={`studio-scene-up-${i}`}>
                        <Upload size={11} />
                        <input type="file" accept="image/*" multiple onChange={(e) => onScenePhotos(i, e)} className="hidden" />
                      </label>
                    </div>
                  </div>
                </div>
              ))}
              {(form.scenes || []).length < MAX_SCENES && (
                <button onClick={addScene} type="button" data-testid="studio-scene-add" className="btn-ghost inline-flex items-center gap-2 text-sm">
                  <Plus size={14} /> Add scene ({(form.scenes || []).length}/{MAX_SCENES})
                </button>
              )}
            </div>
          )}

          {tab === "media" && (
            <>
              <F l="Cover image">
                <div className="flex gap-3 items-start">
                  {form.cover_image ? (
                    <div className="relative">
                      <img src={form.cover_image} alt="" className="w-40 h-24 object-cover rounded border" style={{ borderColor: "var(--gio-border)" }} />
                      <button onClick={() => updateField("cover_image", "")} type="button" className="absolute top-1 right-1 bg-black/80 rounded p-1"><Trash2 size={10} /></button>
                    </div>
                  ) : (
                    <div className="w-40 h-24 rounded border-2 border-dashed flex items-center justify-center" style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-3)" }}><ImageIcon size={20} /></div>
                  )}
                  <label className="btn-ghost cursor-pointer text-xs inline-flex items-center gap-1.5" data-testid="studio-f-cover-upload">
                    <Upload size={11} /> Upload cover
                    <input type="file" accept="image/*" onChange={onUploadCover} className="hidden" />
                  </label>
                </div>
              </F>
              <F l={`General photos · ${form.photos?.length || 0}`}>
                <div className="grid grid-cols-6 gap-2">
                  {(form.photos || []).map((u, i) => (
                    <div key={i} className="relative group rounded overflow-hidden" data-testid={`studio-f-photo-${i}`}>
                      <img src={u} alt="" className="w-full h-16 object-cover" />
                      <button onClick={() => updateField("photos", form.photos.filter((_, j) => j !== i))} type="button"
                        className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white">
                        <Trash2 size={11} />
                      </button>
                    </div>
                  ))}
                  <label className="flex flex-col items-center justify-center h-16 rounded border-2 border-dashed cursor-pointer hover:bg-[var(--gio-primary-soft)]"
                    style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }} data-testid="studio-f-photos-upload">
                    <Upload size={14} />
                    <span className="text-[9px] font-bold uppercase mt-0.5">Upload</span>
                    <input type="file" accept="image/*" multiple onChange={onUploadPhotos} className="hidden" />
                  </label>
                </div>
              </F>
              <F l="Notes">
                <textarea data-testid="studio-f-notes" value={form.notes} onChange={(e) => updateField("notes", e.target.value)} rows={4} className="gio-input" />
              </F>
            </>
          )}
        </div>

        <div className="p-5 border-t border-zinc-900 flex gap-3">
          <button onClick={onSave} data-testid="studio-f-save" className="btn-primary flex-1">{editing ? "Save changes" : "Create lead"}</button>
          <button onClick={onClose} data-testid="studio-f-cancel" className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function F({ l, children }) {
  return <div><div className="overline mb-1.5">{l}</div>{children}</div>;
}

function Toggle({ icon: Icon, label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition"
      style={{
        borderColor: checked ? "var(--gio-secondary)" : "var(--gio-border)",
        background: checked ? "rgba(20,184,166,0.10)" : "transparent",
        color: checked ? "#fff" : "var(--gio-muted)",
      }}
      data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-[color:var(--gio-secondary)]" />
      <Icon size={13} style={{ color: checked ? "var(--gio-secondary)" : "var(--gio-muted-3)" }} />
      <span className="text-[12px]">{label}</span>
    </label>
  );
}
